import { useEffect, useState } from "react";
import { Link } from "react-router";

import { OpenInNew } from "@mui/icons-material";
import { Button, CircularProgress, Tooltip } from "@mui/material";

import _ from "lodash";
import { ORDER_TO_INVENTORY_FROM_ON_DEMAND } from "src/constants/permissions";

import { AddressSearch } from "@features/addresses";
import { distUrl } from "@features/navigation/helpers";
import { AddressFavoriteSet } from "@models/AddressFavoriteSet";
import useRoleIs from "@utils/useRoleIs";

import { useCurrentOrderSet } from "../data/CurrentOrderSetContext";
import useAddOrders from "../data/mutations/useAddOrders";
import DistRestockOptions from "./DistRestockOptions";
import FavoriteAddressSetsMenuButton from "./FavoriteAddressSetsMenuButton";
import NewBranchAddressButton from "./NewBranchAddressButton";
import RestockOrdersButton from "./RestockOrdersButton";

const OrderSetAddressSection = () => {
  const roleIs = useRoleIs();
  const addOrders = useAddOrders();
  const { orderSet, orders } = useCurrentOrderSet();
  const orderType = orderSet.type;
  const [queuedAddressIds, setQueuedAddressIds] = useState<string[]>([]);

  const handleCreateOrders = (addressIds: string[]) => {
    setQueuedAddressIds((ids) => [...ids, ...addressIds]);
    addOrders.mutate(addressIds, {
      onError: () => setQueuedAddressIds((ids) => _.xor(ids, addressIds)),
    });
  };

  const handleCreateFromFavoriteSet = (favoriteSet: AddressFavoriteSet) => {
    const addressIds = favoriteSet.addresses
      .filter((a) => a.isActive)
      .map((a) => a.id);
    handleCreateOrders(addressIds);
  };

  const hasProgram = !!orderSet.program;
  const allowToInventoryOrders =
    orderType === "pre-order" ||
    (orderType === "on-demand" &&
      roleIs(ORDER_TO_INVENTORY_FROM_ON_DEMAND) &&
      hasProgram);

  const reasonForDisabled =
    orderType === "on-demand" && !hasProgram
      ? "The order-set must have a program to add re-stock orders."
      : "Cannot add re-stock orders for on-demand order-sets.";

  const orderSetAddressIds = orders.flatMap((o) =>
    o.address ? [o.address.id] : []
  );

  useEffect(() => {
    if (orders.length > 0) {
      setQueuedAddressIds((ids) =>
        ids.filter((id) => !orders.some((o) => o.address?.id === id))
      );
    }
  }, [orders]);

  if (orderSet.isForDistributor && orderSet.allRegions) {
    return <DistRestockOptions />;
  }

  return (
    <div className="space-y-3">
      <AddressSearch
        multiple
        autoFocus
        orderContext={{
          forDistributor: orderSet.isForDistributor,
          isCouponSweepstakes: orderSet.isCouponSweepstakes,
          marketId: orderSet.market?.id,
          territoryId: orderSet.territory?.id,
        }}
        value={[]}
        onChange={(addresses) =>
          handleCreateOrders([addresses.at(-1)?.id].filter(Boolean) as string[])
        }
        renderTags={() => null}
        getOptionDisabled={(option) =>
          orderSetAddressIds.includes(option.id) ||
          queuedAddressIds.includes(option.id)
        }
      />
      {!orderSet.isForDistributor && (
        <div className="flex gap-2 mt-3">
          <div className="flex-1">
            <FavoriteAddressSetsMenuButton
              handleAddAddressSet={handleCreateFromFavoriteSet}
            />
          </div>
          {!roleIs("field1", "planning-tool-supplier") && (
            <Tooltip title={allowToInventoryOrders ? "" : reasonForDisabled}>
              <div className="flex-1">
                <RestockOrdersButton disabled={!allowToInventoryOrders} />
              </div>
            </Tooltip>
          )}
        </div>
      )}
      {orderSet.isForDistributor && (
        <div className="flex gap-4">
          <NewBranchAddressButton handleCreateOrders={handleCreateOrders} />
          <Button
            color="secondary"
            endIcon={<OpenInNew />}
            component={Link}
            to={distUrl("/address-book")}
            target="_blank"
          >
            Address Book
          </Button>
        </div>
      )}
      {queuedAddressIds.length > 0 && (
        <div className="bg-neutral-100 p-3 rounded-lg text-neutral-600 tracking-wide text-base flex items-center justify-between">
          <div>Creating orders...</div>
          <CircularProgress size={24} />
        </div>
      )}
    </div>
  );
};

export default OrderSetAddressSection;
